import { i18n, localizePersonName } from "@dehub/theme";

/**
 * System seed / bot actors come back from the API as "DeHUB Auto" (or close
 * variants). Show a stable product label instead of the raw DB name.
 */
function normalizeSystemActorName(name: string): string {
  const trimmed = name.trim();
  if (!trimmed || trimmed === "-") return trimmed;
  const label = i18n.t("common:systemAuto", { defaultValue: "System auto" });
  // Full: "DeHUB Auto", "Dehub Auto", "DEHUB AUTO"
  if (/^dehub\s+auto$/i.test(trimmed)) return label;
  // Abbreviated renderName: lastInitial + "." + firstName → "D.Auto"
  if (/^d\.auto$/i.test(trimmed)) return label;
  // Already-localized / capitalize-mangled forms
  if (/^систем\s+авто$/i.test(trimmed)) return label;
  if (/^system\s+auto$/i.test(trimmed)) return label;
  return trimmed;
}


/**
 * Short display name: "Б.Эрдэнэсувд" under the mn locale, "E.ERDENESUVD" under
 * en/ko/zh. Surname initial, dot, given name -- the format itself is unchanged.
 *
 * Under a Latin locale the name is the spelling the user stored on their profile
 * if they entered one, and a transliteration of the Cyrillic name otherwise.
 * Call sites pass the same user object as before and need no change.
 */
export const renderName = (user: any | null | undefined = null): string => {
  if (!user) return "-";

  const localized = localizePersonName(user, i18n.language);
  const firstName = localized.firstName?.trim();
  const lastInitial = localized.lastName?.trim().charAt(0);

  if (!firstName && !lastInitial) return "-";
  if (!lastInitial) return normalizeSystemActorName(firstName || "-");

  return normalizeSystemActorName(`${lastInitial}.${firstName || "-"}`);
};

/**
 * Full display name: "Батбаяр Эрдэнэсувд" under the mn locale, "BATBAYAR
 * ERDENESUVD" under en/ko/zh. Surname first, then the given name -- the order the
 * admin screens use. Use it where the abbreviated renderName form would lose
 * information, instead of concatenating the fields by hand: a hand-built name
 * stays Cyrillic under every locale.
 */
export const renderFullName = (user: any | null | undefined = null): string => {
  if (!user) return "-";

  const localized = localizePersonName(user, i18n.language);
  const parts = [localized.lastName?.trim(), localized.firstName?.trim()].filter(Boolean);

  return normalizeSystemActorName(parts.length ? parts.join(" ") : "-");
};
